/**
 * @fileoverview Zod validation schemas for API requests and responses
 *
 * This module provides runtime validation for data crossing application boundaries,
 * including song generation API requests and responses from the Tempolor service.
 *
 * @module lib/validation
 */

import { z } from 'zod'

/**
 * Song Generation Request Schemas
 */

// Instrumental generation request
// Uses /instrumental/generate endpoint with TemPolor "i" model series
// NOTE: The "i" in model names (e.g., "TemPolor i3.5") indicates INSTRUMENTAL (no vocals)
// These models generate music without vocals or lyrics
export const instrumentalGenerationSchema = z.object({
  prompt: z
    .string()
    .min(1, 'Prompt is required')
    .max(500, 'Prompt must be 500 characters or less')
    .trim(),
  model: z
    .enum(['TemPolor i3.5', 'TemPolor i3.0', 'TemPolor i2.0'])
    .default('TemPolor i3.5'), // Default to latest instrumental model
})

export type InstrumentalGenerationInput = z.infer<typeof instrumentalGenerationSchema>

// Song generation request (with vocals)
// Uses /song/generate endpoint with TemPolor "v" model series
// NOTE: The "v" in model names (e.g., "TemPolor v3.5") indicates VOCAL/SONG models
// These models generate music WITH vocals and lyrics
export const songGenerationSchema = z.object({
  prompt: z
    .string()
    .min(1, 'Prompt is required')
    .max(500, 'Prompt must be 500 characters or less')
    .trim(),
  lyrics: z
    .string()
    .max(3000, 'Lyrics must be 3000 characters or less')
    .optional()
    .transform((val) => (val?.trim() || undefined)),
  voiceId: z
    .string()
    .max(100, 'Voice ID must be 100 characters or less')
    .optional()
    .transform((val) => (val?.trim() || undefined)),
  title: z
    .string()
    .max(200, 'Title must be 200 characters or less')
    .optional()
    .transform((val) => (val?.trim() || undefined)),
  tags: z
    .string()
    .max(300, 'Tags must be 300 characters or less')
    .optional()
    .transform((val) => (val?.trim() || undefined)),
  makeInstrumental: z
    .boolean()
    .optional()
    .default(false),
  model: z
    .enum(['TemPolor v3.5', 'TemPolor v3.0', 'TemPolor v2.0'])
    .default('TemPolor v3.5'), // Default to latest song model (with vocals)
}).refine(
  (data) => {
    // If not instrumental, lyrics are required
    if (!data.makeInstrumental && !data.lyrics) {
      return false
    }
    return true
  },
  {
    message: 'Lyrics are required unless generating instrumental',
    path: ['lyrics'],
  }
)

export type SongGenerationInput = z.infer<typeof songGenerationSchema>

// Stem splitting request
export const stemSplittingSchema = z.object({
  sourceUrl: z
    .string()
    .url('Must be a valid URL')
    .refine((url) => {
      try {
        const parsed = new URL(url)
        // Ensure it's https
        return parsed.protocol === 'https:'
      } catch {
        return false
      }
    }, 'Must be a secure HTTPS URL'),
})

export type StemSplittingInput = z.infer<typeof stemSplittingSchema>

/**
 * Song Generation Response Schemas
 */

// Tempolor API generation status
const generationStatusValues = ['pending', 'processing', 'completed', 'failed'] as const
export const generationStatusSchema = z.enum(generationStatusValues)

export type GenerationStatus = z.infer<typeof generationStatusSchema>

// Tempolor API response for generation creation
export const temporGenerationResponseSchema = z.object({
  _id: z.string(),
  type: z.enum(['instrumental', 'song', 'lyrics', 'stems']),
  status: generationStatusSchema,
  temporRequestId: z.string().optional(),
  audioUrl: z.string().url().optional(),
  error: z.string().optional(),
  createdAt: z.number(),
})

export type TemporGenerationResponse = z.infer<typeof temporGenerationResponseSchema>

// Tempolor webhook callback data (from Tempolor API)
// Updated to match actual webhook payload structure from Tempolor docs
export const temporWebhookSchema = z.object({
  request_id: z.string(),
  status: z.enum(['completed', 'failed']),
  event: z.enum(['audio_complete', 'wav_complete', 'lrcsections_complete']).optional(),
  data: z
    .object({
      audio_url: z.string().url().optional(), // MP3 audio (3-day validity)
      audio_hi_url: z.string().url().optional(), // WAV lossless audio (3-day validity)
      lyrics: z.string().optional(), // Full lyrics text
      lyrics_sections: z
        .array(
          z.object({
            start: z.number(), // Start timestamp in seconds
            end: z.number(), // End timestamp in seconds
            text: z.string(), // Lyric line text
          })
        )
        .optional(), // Time-aligned lyrics
      stems: z
        .object({
          vocal: z.string().url().optional(),
          instrumental: z.string().url().optional(),
          bass: z.string().url().optional(),
          drums: z.string().url().optional(),
          guitar: z.string().url().optional(),
          piano: z.string().url().optional(),
          other: z.string().url().optional(),
        })
        .optional(),
    })
    .optional(),
  error: z.string().optional(),
})

export type TemporWebhookPayload = z.infer<typeof temporWebhookSchema>

/**
 * Helper function to safely parse and validate data
 */
export function validateOrThrow<T>(schema: z.ZodSchema<T>, data: unknown): T {
  return schema.parse(data)
}

/**
 * Helper function to safely parse with error handling
 */
export function validateSafe<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; error: string } {
  const result = schema.safeParse(data)
  if (result.success) {
    return { success: true, data: result.data }
  } else {
    return {
      success: false,
      error: result.error.issues.map((e: z.ZodIssue) => `${e.path.join('.')}: ${e.message}`).join('; '),
    }
  }
}
